import {storeInstance} from '@aofl/store/dist/legacy';
import {productOrder, sdoNs, statsProducts} from './constants-enumerate';
import {deepAssign} from '@aofl/object-utils';
import {getLocaleDate} from './get-locale-date';
import {dateTimeFormatter} from '@modules/i18n-datetime';
import {getSortedProducts} from '@modules/product-helper';

const DEFAULT_DATA = {
  id: 0,
  first_name: '',
  last_name: '',
  external_id: '',
  preferred_communication: 'email',
  phone: '',
  email: '',
  contact_phone: '',
  contact_email: '',
  classes: [],
  schools: [],
  products: [],
  group_info: {},
  is_third_party_connected: false,
  profile_id: 0,
  managed_by: null,
  archived_timestamp: null,
};

const sdo = {
  namespace: sdoNs.TEACHER_INFO,
  mutations: {
    init() {
      return Object.assign({}, DEFAULT_DATA);
    },
    setData(subState, {
      id,
      first_name,
      last_name,
      external_id,
      preferred_communication,
      phone,
      email,
      classes,
      products,
      is_third_party_connected,
      group_info,
      schools,
      profile_id,
      contact_phone,
      contact_email,
      managed_by,
      archived_timestamp,
    }) { // eslint-disable-line
      return Object.assign({}, subState, {
        id,
        first_name,
        last_name,
        external_id,
        preferred_communication,
        phone,
        email,
        contact_phone,
        contact_email,
        classes,
        schools,
        products: products || [],
        group_info: group_info || {},
        is_third_party_connected,
        profile_id,
        managed_by,
        archived_timestamp,
      });
    },
    addGroups(subState, groups) {
      const allGroups = {...(subState.group_info || {})};
      groups.map((item) => { allGroups[item.id] = item; });

      return Object.assign({}, subState, {group_info: allGroups});
    },
    addClass(subState, classItem) {
      const classes = [
        ...subState.classes,
        classItem
      ];
      const uniqueClasses = {};
      classes.map((item) => {
        uniqueClasses[item.id] = item;
      });
      return Object.assign({}, subState, {
        classes: Object.values(uniqueClasses)
      });
    },
    removeClass(subState, index) {
      return Object.assign({}, subState, {
        classes: [
          ...subState.classes.slice(0, index),
          ...subState.classes.slice(index + 1)
        ]
      });
    }
  },
  decorators: [
    (nextState) => { // $classesIds
      const state = storeInstance.getState();
      const subState = state[sdoNs.TEACHER_INFO];
      const nextSubState = nextState[sdoNs.TEACHER_INFO];

      if (typeof nextState.$classesIds === 'undefined' ||
      nextSubState.classes !== subState.classes) {
        const $classesIds = nextSubState.classes.map((item) => item.id);

        nextState = deepAssign(nextState, sdoNs.TEACHER_INFO, {$classesIds});
      }

      return nextState;
    },
    (nextState) => { // $products
      const state = storeInstance.getState();
      const subState = state[sdoNs.TEACHER_INFO];
      const nextSubState = nextState[sdoNs.TEACHER_INFO];

      if (typeof nextState.$products === 'undefined' ||
      nextSubState.products !== subState.products ||
      nextState[sdoNs.SESSION].locale !== state[sdoNs.SESSION].locale) {
        let $qrEnabled = false;
        const $products = nextSubState.products.map((item) => {
          if (
            item.product === statsProducts.MY_READING_ACADEMY ||
            item.product === statsProducts.MY_MATH_ACADEMY ||
            item.product === statsProducts.MY_READING_ACADEMY_ES
          ) {
            $qrEnabled = true;
          }
          return Object.assign({}, item, {
            active: item.status === 'active',
            licenseEndDate: getLocaleDate(item.end_timestamp * 1000, nextState[sdoNs.SESSION].locale),
            redeemDate: item.redeemed_timestamp ?
              getLocaleDate(item.redeemed_timestamp * 1000, nextState[sdoNs.SESSION].locale) : null
          });
        });
        nextState = deepAssign(
          nextState,
          sdoNs.TEACHER_INFO,
          {
            $products: getSortedProducts($products),
            $qrEnabled
          });
      }

      return nextState;
    },
    (nextState) => { // $classesGroupedBySchool
      const state = storeInstance.getState();
      const subState = state[sdoNs.TEACHER_INFO];
      const nextSubState = nextState[sdoNs.TEACHER_INFO];

      if (
        typeof nextState.$classesGroupedBySchools === 'undefined' ||
        nextSubState.classes !== subState.classes ||
        nextSubState.schools !== subState.schools
      ) {
        const classesGroupedBySchool = {};
        nextSubState.classes.map((item, index) => {
          let pathArr = item.path?.split('.');
          const parentId = pathArr[pathArr.length - 2];
          if (!classesGroupedBySchool[parentId]) {
            classesGroupedBySchool[parentId] = [];
          }
          // start path from current role's level
          const classInfo = {
            ...item,
            index,
            parent_id: parentId,
          };
          classesGroupedBySchool[parentId].push(classInfo);
        });
        nextSubState.schools.map((id) => {
          if (!classesGroupedBySchool[id]) {
            classesGroupedBySchool[id] = [];
          }
        });

        nextState = deepAssign(nextState, sdoNs.TEACHER_INFO, {
          $classesGroupedBySchool: classesGroupedBySchool
        });
      }

      return nextState;
    },
    (nextState) => { // $groupInfo
      const state = storeInstance.getState();
      const subState = state[sdoNs.TEACHER_INFO];
      const nextSubState = nextState[sdoNs.TEACHER_INFO];

      if (typeof nextState.$groupInfo === 'undefined' ||
        nextSubState.group_info !== subState.group_info) {
        const $groupInfo = {};
        Object.values(nextSubState.group_info).map((item) => {
          const fullPath = item.path + item.id + '.';
          let splitPath = fullPath.split('.');
          splitPath.pop();
          // start path from current role's level
          splitPath = splitPath.splice(nextState[sdoNs.SESSION].currentLevel);
          $groupInfo[item.id] = {
            ...item,
            split_path: splitPath.map((id) => parseInt(id, 10))
          };
        });

        nextState = deepAssign(nextState, sdoNs.TEACHER_INFO, {$groupInfo});
      }

      return nextState;
    },
    (nextState) => { // $archivedDate
      const state = storeInstance.getState();
      const subState = state[sdoNs.TEACHER_INFO];
      const nextSubState = nextState[sdoNs.TEACHER_INFO];

      if (
        typeof nextState.$archivedDate === 'undefined'
        ||
        nextSubState.archived_timestamp !== subState.archived_timestamp
      ) {
        let $archivedDate = null;
        if (nextSubState.archived_timestamp) {
          $archivedDate = dateTimeFormatter.format(nextSubState.archived_timestamp * 1000);
        }
        nextState = deepAssign(nextState, sdoNs.TEACHER_INFO, {$archivedDate});
      }

      return nextState;
    },
  ]
};

storeInstance.addState(sdo);
